<section class="decision-calendar" *ngIf="!loading && !error; else calendarState">
  <header class="decision-calendar__header">
    <div>
      <p class="decision-calendar__eyebrow">{{ lang.translate('home.calendar.eyebrow') }}</p>
      <h2 class="decision-calendar__title">{{ lang.translate('home.calendar.title') }}</h2>
      <p class="decision-calendar__copy">{{ lang.translate('home.calendar.description') }}</p>
    </div>
    <span class="decision-calendar__count">{{ calendarViewModel.totalEntries }}</span>
  </header>

  <div class="decision-calendar__toolbar">
    <div class="decision-calendar__granularity" role="tablist">
      <button
        type="button"
        class="decision-calendar__granularity-button"
        *ngFor="let item of granularities"
        [class.is-active]="granularity === item"
        [attr.data-granularity]="item"
        [attr.aria-pressed]="granularity === item"
        (click)="onSelectGranularity(item)"
      >
        {{ lang.translate('home.calendar.granularity.' + item) }}
      </button>
    </div>

    <div class="decision-calendar__period-controls">
      <button type="button" class="btn btn-outline-secondary btn-sm" (click)="onNavigatePeriod(-1)">
        {{ lang.translate('home.calendar.previous') }}
      </button>
      <span class="decision-calendar__period-title">{{ periodTitle }}</span>
      <button type="button" class="btn btn-outline-secondary btn-sm" (click)="onJumpToToday()">
        {{ lang.translate('home.calendar.today') }}
      </button>
      <button type="button" class="btn btn-outline-secondary btn-sm" (click)="onNavigatePeriod(1)">
        {{ lang.translate('home.calendar.next') }}
      </button>
    </div>
  </div>

  <app-home-decision-calendar-agenda
    [viewModel]="agendaViewModel"
    [monthTitle]="agendaMonthTitle"
    [selectedDecisionId]="selectedDecisionId"
    (monthNavigate)="onAgendaNavigate($event)"
    (todayRequest)="onJumpToToday()"
    (selectDecision)="onSelectDecision($event)"
  ></app-home-decision-calendar-agenda>

  <ng-container *ngIf="calendarViewModel as viewModel">
    <div class="decision-calendar__desktop-views">
    <div class="decision-calendar__empty" *ngIf="!viewModel.totalEntries && !viewModel.undatedEntries.length">
      {{ lang.translate('home.calendar.empty') }}
    </div>

    <ng-container *ngIf="granularity === 'month'; else weekOrYearView">
      <div class="decision-calendar__month-layout" [class.decision-calendar__month-layout--with-panel]="!!selectedDay">
        <div class="decision-calendar__month-board">
          <div class="decision-calendar__weekdays">
            <span *ngFor="let day of viewModel.weekDayDates">{{ day | date: 'EEE' }}</span>
          </div>

          <div class="decision-calendar__grid decision-calendar__grid--month">
            <article
              class="decision-calendar__day"
              *ngFor="let day of viewModel.days; trackBy: trackDay"
              [class.is-muted]="!day.inCurrentPeriod"
              [class.is-today]="day.isToday"
              [class.is-selected]="day.isSelected"
              [class.is-clickable]="day.entries.length > 0"
              [class.is-expanded]="selectedDay?.dayKey === day.dayKey"
              (click)="onSelectDay(day.dayKey, day.entries.length > 0)"
            >
              <header class="decision-calendar__day-header">
                <span class="decision-calendar__day-number">{{ day.date | date: 'd' }}</span>
                <span class="decision-calendar__day-total" *ngIf="day.entries.length">{{ day.entries.length }}</span>
              </header>

              <div class="decision-calendar__day-states" *ngIf="day.entries.length; else emptyDay">
                <span
                  class="decision-calendar__day-state"
                  *ngFor="let summary of getVisibleDayStateSummaries(day)"
                  [attr.data-state]="summary.state"
                  [attr.aria-label]="getStateCountLabel(summary.state, summary.count)"
                  [attr.title]="getStateCountLabel(summary.state, summary.count)"
                >
                  <i [class]="'fa ' + getStateIconClass(summary.state)" aria-hidden="true"></i>
                  <span>{{ summary.count }}</span>
                </span>
                <span class="decision-calendar__day-more" *ngIf="getDayStateOverflowCount(day) as overflow">
                  +{{ overflow }} {{ lang.translate('home.calendar.moreEntries') }}
                </span>
              </div>

              <ng-template #emptyDay>
                <div class="decision-calendar__day-empty"></div>
              </ng-template>
            </article>
          </div>
        </div>

        <aside class="decision-calendar__day-panel" *ngIf="selectedDay as activeDay" #dayPanel>
          <header class="decision-calendar__day-panel-header">
            <div>
              <p class="decision-calendar__eyebrow">{{ activeDay.date | date: 'fullDate' }}</p>
              <h3 class="decision-calendar__day-panel-title">{{ activeDay.date | date: 'd MMMM y' }}</h3>
            </div>
            <span class="decision-calendar__day-total">{{ activeDay.entries.length }}</span>
          </header>

          <div class="decision-calendar__day-panel-items">
            <button
              type="button"
              class="decision-calendar__entry decision-calendar__entry--detail"
              *ngFor="let entry of activeDay.entries; trackBy: trackEntry"
              [class.is-selected]="selectedDecisionId === entry.decisionId"
              [attr.data-decision-id]="entry.decisionId"
              [attr.data-state]="entry.state"
              (click)="onSelectDecision(entry.decisionId)"
            >
              <span class="decision-calendar__entry-icon" aria-hidden="true">
                <i [class]="'fa ' + getStateIconClass(entry.state)"></i>
              </span>
              <span class="decision-calendar__entry-copy">
                <span class="decision-calendar__entry-title">{{ formatDecisionTitle(entry.title) }}</span>
                <span class="decision-calendar__entry-meta">{{ getStateLabel(entry.state) }}</span>
                <span class="decision-calendar__entry-reason">{{ entry.shortReason }}</span>
                <span class="decision-calendar__entry-secondary" *ngIf="entry.ownerLabel">{{ entry.ownerLabel }}</span>
              </span>
            </button>
          </div>
        </aside>
      </div>
    </ng-container>

    <ng-template #weekOrYearView>
      <ng-container *ngIf="granularity === 'week'; else yearView">
        <div class="decision-calendar__week-board">
          <article class="decision-calendar__week-day" *ngFor="let day of viewModel.days; trackBy: trackDay">
            <header class="decision-calendar__week-day-header">
              <div>
                <p class="decision-calendar__eyebrow">{{ day.date | date: 'EEEE' }}</p>
                <h3 class="decision-calendar__week-day-title">{{ day.date | date: 'd MMM' }}</h3>
              </div>
              <span class="decision-calendar__day-total" *ngIf="day.entries.length">{{ day.entries.length }}</span>
            </header>

            <div class="decision-calendar__week-day-empty" *ngIf="!day.entries.length">
              {{ lang.translate('home.calendar.emptyDay') }}
            </div>

            <div class="decision-calendar__week-day-items" *ngIf="day.entries.length">
              <button
                type="button"
                class="decision-calendar__entry decision-calendar__entry--detail"
                *ngFor="let entry of day.entries; trackBy: trackEntry"
                [class.is-selected]="selectedDecisionId === entry.decisionId"
                [attr.data-decision-id]="entry.decisionId"
                [attr.data-state]="entry.state"
                (click)="onSelectDayEntry($event, day.dayKey, entry.decisionId)"
              >
                <span class="decision-calendar__entry-icon" aria-hidden="true">
                  <i [class]="'fa ' + getStateIconClass(entry.state)"></i>
                </span>
                <span class="decision-calendar__entry-copy">
                  <span class="decision-calendar__entry-title">{{ formatDecisionTitle(entry.title) }}</span>
                  <span class="decision-calendar__entry-meta">{{ getStateLabel(entry.state) }}</span>
                  <span class="decision-calendar__entry-reason">{{ entry.shortReason }}</span>
                  <span class="decision-calendar__entry-secondary" *ngIf="entry.ownerLabel">{{ entry.ownerLabel }}</span>
                </span>
              </button>
            </div>
          </article>
        </div>
      </ng-container>
    </ng-template>

    <ng-template #yearView>
      <div class="decision-calendar__year-grid">
        <button
          type="button"
          class="decision-calendar__month-card"
          *ngFor="let month of viewModel.months; trackBy: trackMonth"
          (click)="openMonth(month)"
        >
          <div class="decision-calendar__month-header">
            <span class="decision-calendar__month-title">{{ month.monthDate | date: 'MMMM' }}</span>
            <span class="decision-calendar__month-total">{{ month.totalEntries }}</span>
          </div>

          <div class="decision-calendar__month-states" *ngIf="month.totalEntries">
            <span
              class="decision-calendar__month-state"
              *ngFor="let state of getMonthStateKeys(month)"
              [attr.data-state]="state"
              [attr.aria-label]="getStateCountLabel(state, getMonthStateCount(month, state))"
              [attr.title]="getStateCountLabel(state, getMonthStateCount(month, state))"
            >
              <i [class]="'fa ' + getStateIconClass(state)" aria-hidden="true"></i>
              <span>{{ getMonthStateCount(month, state) }}</span>
            </span>
          </div>
        </button>
      </div>
    </ng-template>

    <section class="decision-calendar__undated" *ngIf="viewModel.undatedEntries.length">
      <header class="decision-calendar__undated-header">
        <h3>{{ lang.translate('home.calendar.undated') }}</h3>
        <span>{{ viewModel.undatedEntries.length }}</span>
      </header>
      <div class="decision-calendar__undated-items">
        <button
          type="button"
          class="decision-calendar__entry decision-calendar__entry--detail"
          *ngFor="let entry of viewModel.undatedEntries; trackBy: trackEntry"
          [class.is-selected]="selectedDecisionId === entry.decisionId"
          [attr.data-state]="entry.state"
          (click)="onSelectDecision(entry.decisionId)"
        >
          <span class="decision-calendar__entry-icon" aria-hidden="true">
            <i [class]="'fa ' + getStateIconClass(entry.state)"></i>
          </span>
          <span class="decision-calendar__entry-copy">
            <span class="decision-calendar__entry-title">{{ formatDecisionTitle(entry.title) }}</span>
            <span class="decision-calendar__entry-meta">{{ getStateLabel(entry.state) }}</span>
          </span>
        </button>
      </div>
    </section>
    </div>
  </ng-container>
</section>

<ng-template #calendarState>
  <div class="decision-calendar decision-calendar--state" *ngIf="error; else calendarLoading">
    <p class="decision-calendar__state-title">{{ error.title }}</p>
    <p class="decision-calendar__state-copy">{{ error.message }}</p>
    <button type="button" class="btn btn-outline-primary" (click)="onRetry()">
      {{ lang.translate('home.general.retry') }}
    </button>
  </div>

  <ng-template #calendarLoading>
    <div class="decision-calendar decision-calendar--state" role="status">
      <p class="decision-calendar__state-copy">{{ lang.translate('home.general.loading') }}</p>
    </div>
  </ng-template>
</ng-template>
